import React, { useContext } from 'react';
import classnames from 'classnames';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { GridContainer } from 'formlabs-web-components/Grid';
import { Button } from 'formlabs-web-components/Link';
import {
  Accordion,
  AccordionItem,
  StoreCarousel,
  Picture
} from 'formlabs-web-components/Media';
import { Spinner } from 'formlabs-web-components/Navigation';
import { Header } from 'formlabs-web-components/Type';
import { Breadcrumbs } from '../navigation';
import LeadTime from './LeadTime';
import Price from './Price';
import useFetchFilter from '../../hooks/useFetchFilter';
import CountryContext from '../../context/country';

import './ProductDetailPlugin.module.css';

const cdnUrl = `${
  process.env.RAZZLE_MAGENTO_MEDIA_URL
}/catalog/product/600x600`;

const Compatibility = ({
  filterBiocompatible = null,
  filter_3dPrinter = null,
  filterMaterialFamily = null,
  meta = null
}) => {
  const { language } = useContext(CountryContext);
  const { t } = useTranslation();
  const { storeId } = useSelector(state => ({
    storeId: state.store.id
  }));
  const isBiocompatible = filterBiocompatible !== null;
  const { loading, data } = useFetchFilter('filter_3d_printer', storeId);

  if (loading || !data) {
    return (
      <p>
        <Spinner size={4} />
      </p>
    );
  }

  const compatiblePrinterIds = filter_3dPrinter
    ? filter_3dPrinter.split(',')
    : [];

  const printers = compatiblePrinterIds
    .map(id => data.options.find(option => option.value === id).label)
    .join(', ');

  return (
    <>
      {printers && <p>{t('Compatible with: {{printers}}', { printers })}</p>}

      {isBiocompatible && <p>{t('Is certified biocompatible.')}</p>}

      {filterMaterialFamily && (
        <p>
          <Button
            color="borderless-blue"
            arrow="right-caret"
            openInNewWindow
            to={`https://support.formlabs.com/s/article/Choosing-the-Right-Material?language=${language}`}
          >
            {t(
              'Additional resin compatibility information is available on our Support site'
            )}
          </Button>
        </p>
      )}

      {meta}
    </>
  );
};

const ProductDetailHeader = ({
  actions = null,
  activeBundleOptions = [],
  additionalPriceText = null,
  amount = 1,
  attributes,
  baseUrl,
  bundleOptions = null,
  category,
  media,
  meta = null,
  name,
  price,
  sku,
  showAvailability = true,
  isMinPrice = false
}) => {
  const { t } = useTranslation();
  const { path } = useContext(CountryContext);

  const pictures = media
    ? media.filter(item => item.media_type === 'image')
    : [];

  const navigationEntries = [
    {
      url: '',
      // Nested HashRouter will add baseUrl after the # in the url. The a-tag routes back to parent router
      name: (
        <a href={baseUrl} target="_self">
          {t('Store')}
        </a>
      )
    }
  ];
  if (category) {
    navigationEntries.push({
      name: category.name,
      url: `${baseUrl}${category.attributes.urlPath}/`
    });
  }
  if (name) {
    navigationEntries.push({ name });
  }

  const hasLeadTime = attributes.leadTime && attributes.leadTime > 0;

  return (
    <div styleName={classnames('page', 'light')}>
      <GridContainer styleName="header">
        <div styleName="header-title">
          <div styleName="menu">
            <Breadcrumbs entries={navigationEntries} noPadding />
          </div>
          <Header level={1} displayAs={3} weight={700}>
            {name}
          </Header>
          <p styleName="sku">{sku}</p>
          {hasLeadTime && <LeadTime value={attributes.leadTime} sku={sku} />}
        </div>
        <div styleName="header-price">
          <Price styleName="price" price={price} isMinPrice={isMinPrice} />
          {amount > 1 && (
            <div styleName="price-per">
              <p>
                <Price
                  styleName="inline-price"
                  price={price / amount}
                  showVatMessage={false}
                />
                {` ${t('per item')}`}
              </p>
            </div>
          )}
          {additionalPriceText && (
            <div styleName="additionalPriceText">{additionalPriceText}</div>
          )}
        </div>
        <div styleName="header-images">
          <StoreCarousel controlsPosition="internal">
            {pictures.map(picture => (
              <Picture
                fullBleed
                key={picture.id}
                largeImage={`${cdnUrl}${picture.file}`}
              />
            ))}
          </StoreCarousel>
        </div>
        {actions}
        <div styleName="header-content">
          <Accordion type="filter-list">
            <AccordionItem title={t('Product Description')} open={true}>
              <div
                styleName="long-desc"
                dangerouslySetInnerHTML={{
                  __html: attributes.description
                }}
              />
            </AccordionItem>

            <AccordionItem title={t('What’s Included')} open={true}>
              <ul styleName="included">
                {activeBundleOptions && activeBundleOptions.length ? (
                  activeBundleOptions.map(option =>
                    option.map(entry => <li key={entry.id}>{entry.name}</li>)
                  )
                ) : bundleOptions ? (
                  bundleOptions
                    .filter(o => o.required)
                    .map(o => <li key={o.title}>{o.title}</li>)
                ) : (
                  <li>{name}</li>
                )}
              </ul>
            </AccordionItem>

            {attributes.filterBiocompatible || attributes.filter_3dPrinter ? (
              <AccordionItem title={t('Compatibility')} open={false}>
                <Compatibility meta={meta} {...attributes} />
              </AccordionItem>
            ) : null}

            <AccordionItem
              title={
                showAvailability ? t('Availability & Shipping') : t('Shipping')
              }
              open={false}
            >
              {showAvailability && (
                <p>
                  {hasLeadTime
                    ? t('Available to ship in {{count}} days.', {
                        count: attributes.leadTime * (7 / 5) // adjust leadtime for users to include weekends
                      })
                    : t('Available to ship immediately.')}
                </p>
              )}

              <p>
                {t(
                  'Shipping to the Asia-Pacific, Eastern Europe, Latin America, the Middle East, or Africa?'
                )}
                <br />
                <Button
                  color="borderless-blue"
                  arrow="right-caret"
                  to={`https://formlabs.com${path}company/partners/`}
                >
                  {t('Order via our distribution partners')}
                </Button>
              </p>
            </AccordionItem>
          </Accordion>
        </div>
      </GridContainer>
    </div>
  );
};

export default ProductDetailHeader;
